// vCard 3.0 (RFC 2426) generation and download.
//
// Replaces the `vcards-js` package, which emitted non-conformant cards that
// newer versions of iOS Contacts reject:
//   - content lines were never folded (a photo produced one 160KB+ line)
//   - PHOTO carried the full MIME type (`TYPE=image/png`) instead of `TYPE=PNG`
//
// Delivery is via a Blob object URL rather than `document.location.href =
// "data:text/vcard,..."`, because Safari blocks top-level navigation to
// data: URLs — the tap silently did nothing.

const CRLF = "\r\n";
const MAX_OCTETS = 75; // RFC 2426 section 2.6

// Escape a TEXT value. Order matters: backslashes first.
function escapeText(value) {
  return String(value)
    .replace(/\\/g, "\\\\")
    .replace(/\r\n|\r|\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

// Fold a content line at 75 octets. Continuation lines begin with one space,
// which itself counts toward the octet budget. Never splits a multi-byte
// UTF-8 sequence.
function foldLine(line) {
  const bytes = new TextEncoder().encode(line);
  if (bytes.length <= MAX_OCTETS) return line;

  const decoder = new TextDecoder();
  const chunks = [];
  let pos = 0;
  let budget = MAX_OCTETS;

  while (pos < bytes.length) {
    let end = Math.min(pos + budget, bytes.length);
    // Back off if we landed on a UTF-8 continuation byte (10xxxxxx).
    while (end > pos && end < bytes.length && (bytes[end] & 0xc0) === 0x80) end--;
    chunks.push(decoder.decode(bytes.subarray(pos, end)));
    pos = end;
    budget = MAX_OCTETS - 1; // leading space on continuation lines
  }

  return chunks.join(CRLF + " ");
}

function isBlank(value) {
  return value === undefined || value === null || String(value).trim() === "";
}

// "Wei De Chang" -> { first: "Wei", last: "De Chang" }
function splitName(fullName) {
  const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: "", last: "" };
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}

// "image/png" -> "PNG", "image/jpg" -> "JPEG"
function photoType(mime) {
  const subtype = String(mime || "").split("/")[1] || "";
  const upper = subtype.toUpperCase();
  return upper === "JPG" ? "JPEG" : upper;
}

// RFC 2426 wants a basic-format timestamp with no fractional seconds.
function revStamp() {
  return new Date().toISOString().replace(/\.\d{3}Z$/, "Z");
}

/**
 * Fetch an image and return it as { mime, base64 } for embedding.
 * Returns null (and warns) on failure — a missing photo must not stop the
 * contact from downloading.
 */
export async function fetchPhoto(src) {
  if (isBlank(src)) return null;
  try {
    const response = await fetch(src);
    if (!response.ok) throw new Error("HTTP " + response.status);
    const blob = await response.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    const match = /^data:([^;,]+);base64,(.*)$/.exec(String(dataUrl));
    if (!match) throw new Error("unexpected data URL shape");
    return { mime: match[1], base64: match[2] };
  } catch (error) {
    console.warn("vCard: skipping photo -", error.message);
    return null;
  }
}

/**
 * Build a vCard 3.0 string.
 *
 * @param {object} contact
 * @param {string} contact.name         full display name
 * @param {string} [contact.organization]
 * @param {string} [contact.title]
 * @param {string} [contact.cellPhone]
 * @param {string} [contact.workPhone]
 * @param {string} [contact.workFax]
 * @param {string} [contact.email]
 * @param {string} [contact.url]
 * @param {string} [contact.note]
 * @param {{mime: string, base64: string}} [contact.photo] from fetchPhoto()
 */
export function buildVCard(contact = {}) {
  const { first, last } = splitName(contact.name);
  const lines = ["BEGIN:VCARD", "VERSION:3.0"];

  const add = (property, rawValue) => {
    if (isBlank(rawValue)) return;
    lines.push(foldLine(property + ":" + escapeText(rawValue)));
  };

  // N is structured: family;given;additional;prefix;suffix — the components
  // are escaped individually so the semicolons stay meaningful.
  lines.push(
    foldLine("N:" + escapeText(last) + ";" + escapeText(first) + ";;;")
  );
  add("FN", String(contact.name || "").trim());
  add("ORG", contact.organization);
  add("TITLE", contact.title);
  add("TEL;TYPE=CELL,VOICE", contact.cellPhone);
  add("TEL;TYPE=WORK,VOICE", contact.workPhone);
  add("TEL;TYPE=WORK,FAX", contact.workFax);
  add("EMAIL;TYPE=INTERNET,PREF", contact.email);
  add("URL", contact.url);
  add("NOTE", contact.note);

  const photo = contact.photo;
  if (photo && !isBlank(photo.base64)) {
    const type = photoType(photo.mime);
    const parameters = type ? ";TYPE=" + type : "";
    // Base64 is already safe — escaping it would corrupt the payload.
    lines.push(foldLine("PHOTO;ENCODING=b" + parameters + ":" + photo.base64));
  }

  lines.push("REV:" + revStamp());
  lines.push("END:VCARD");

  return lines.join(CRLF) + CRLF;
}

function safeFilename(name) {
  const cleaned = String(name || "")
    .replace(/[\\/:*?"<>|]/g, "") // illegal on Windows, awkward everywhere
    .replace(/\s+/g, " ")
    .trim();
  return (cleaned || "contact") + ".vcf";
}

/**
 * Trigger a download of the card. Uses a Blob object URL and a synthetic
 * anchor click, which iOS Safari honours; a data: URL navigation does not.
 */
export function downloadVCard(vCardString, name) {
  const blob = new Blob([vCardString], { type: "text/vcard;charset=utf-8" });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = objectUrl;
  anchor.download = safeFilename(name);
  anchor.rel = "noopener";
  anchor.style.display = "none";

  document.body.appendChild(anchor);
  anchor.click();

  // Safari needs the URL to outlive the click.
  setTimeout(() => {
    document.body.removeChild(anchor);
    URL.revokeObjectURL(objectUrl);
  }, 1000);
}

/**
 * Convenience wrapper: fetch the photo, build the card, download it.
 * Photo failures degrade to a card without a photo.
 */
export async function saveContactCard(contact = {}) {
  const photo = await fetchPhoto(contact.profileImg);
  const vCardString = buildVCard({ ...contact, photo });
  downloadVCard(vCardString, contact.name);
  return vCardString;
}
